import React, { useState, useEffect } from "react";
import Button from "./../../../../components/Button/Button"; // Chemin adapté à votre application
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import useScrollToTop from "./../../../../hooks/useScrollToTop";
import {
  getUserById,
  updateUserProfile,
  updateUserProfilePhoto,
  updateNotificationPermission,
} from "./../../../../services/userServices"; // Chemin adapté à votre application
import { FaSave, FaTimes } from "react-icons/fa";
import OneSignal from "react-onesignal";

const Profile = () => {
  useScrollToTop();
  const [user, setUser] = useState({
    id: "",
    nom: "",
    prenom: "",
    email: "",
    photoURL: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]); // Prend le premier fichier sélectionné
  };

  // Vérifier l'état des notifications au chargement de la page
  useEffect(() => {
    const checkNotificationStatus = async () => {
      try {
        const isSubscribed = await OneSignal.isPushNotificationsEnabled();
        setNotificationsEnabled(isSubscribed);
      } catch (error) {
        console.error(
          "Erreur lors de la vérification des notifications :",
          error
        );
      }
    };

    checkNotificationStatus();
  }, []);

  useEffect(() => {
    // Récupérer les informations de l'utilisateur stockées localement
    const userInfo = localStorage.getItem("userInfo")
      ? JSON.parse(localStorage.getItem("userInfo"))
      : null;
    const userId = userInfo?.userId;

    if (userId) {
      // Charger les données de l'utilisateur depuis le serveur
      const fetchUserData = async () => {
        try {
          const userData = await getUserById(userId);
          setUser(userData);
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des données de l'utilisateur:",
            error
          );
          toast.error(
            "Erreur lors de la récupération des données de l'utilisateur."
          );
        }
      };

      fetchUserData();
    }
  }, []);

  useEffect(() => {
    if (user.push_notifications_enabled !== undefined) {
      setNotificationsEnabled(user.push_notifications_enabled); // Utiliser directement la valeur booléenne
    }
  }, [user]);

  const handleEdit = () => {
    setIsEditing(true); // Ouvrir la modale pour modifier les informations personnelles
  };

  const handleCloseModal = () => {
    setIsEditing(false); // Fermer la modale de modification des informations
    setIsUploading(false); // Fermer la modale de modification de la photo
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      const updatedUser = await updateUserProfile(user.id, user, token);
      localStorage.setItem("userInfo", JSON.stringify(updatedUser));
      setUser(updatedUser);
      setIsEditing(false);
      toast.success("Profil mis à jour avec succès!");
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des données de l'utilisateur:",
        error
      );
      toast.error(
        "Erreur lors de la mise à jour des données de l'utilisateur."
      );
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value }); // Mettre à jour l'état local avec les nouvelles valeurs des inputs
  };

  const handleUploadModal = () => {
    setIsUploading(true); // Ouvrir la modale pour le changement de photo
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Veuillez sélectionner une image à télécharger.");
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await updateUserProfilePhoto(
        user.id,
        selectedFile,
        token
      );
      if (response && response.data.imageUrl) {
        setUser({ ...user, photoURL: response.data.imageUrl });
        toast.success("Photo de profil mise à jour avec succès!");
      } else {
        throw new Error("Image not updated properly.");
      }
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image :", error);
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setIsUploading(false);
    }
  };

  // Gérer le basculement des notifications push
  const handleToggleNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Token utilisateur introuvable.");
        return;
      }

      const newPermission = !notificationsEnabled;

      if (newPermission) {
        await OneSignal.init({ appId: "VOTRE_APP_ID_ONESIGNAL" });
        OneSignal.showSlidedownPrompt();
        const isSubscribed = await OneSignal.isPushNotificationsEnabled();

        if (isSubscribed) {
          const playerId = await OneSignal.getUserId();
          console.log("Player ID :", playerId);

          await updateNotificationPermission(
            user.id,
            newPermission,
            playerId,
            token
          );
          setNotificationsEnabled(true);
          toast.success("Notifications activées !");
        } else {
          toast.error("L'utilisateur a refusé les notifications.");
        }
      } else {
        await OneSignal.setSubscription(false);
        await updateNotificationPermission(user.id, false, null, token);
        setNotificationsEnabled(false);
        toast.success("Notifications désactivées.");
      }
    } catch (error) {
      console.error("Erreur lors de la gestion des notifications :", error);
      toast.error("Une erreur s'est produite.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="bg-gray-600 text-gray-200 p-8 rounded-lg shadow-lg w-full max-w-md">
        <h1 className="text-2xl font-semibold mb-4 text-center">
          Profil Utilisateur
        </h1>
        <div className="flex flex-col items-center mb-4">
          <img
            src={user.photoURL || "https://via.placeholder.com/150"}
            alt="Avatar de l'utilisateur"
            className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-gray-400 shadow-lg"
          />
          <div>
            <Button
              text="Modifier Photo"
              onClick={handleUploadModal}
              className="mb-4 bg-gray-700 text-gray-200"
            />
            {isUploading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-gray-700 text-gray-200 p-6 rounded-lg shadow-xl max-w-lg w-full">
                  <h2 className="text-xl font-bold uppercase p-3 mb-4 text-center text-gray-200 bg-primary rounded-md shadow">
                    Changer la Photo de Profil
                  </h2>
                  <form
                    onSubmit={handleUploadSubmit}
                    className="flex flex-col items-center space-y-4 w-full"
                  >
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept="image/*"
                      className="my-2 text-gray-200"
                    />
                    <div className="flex flex-auto gap-4">
                      <Button
                        text="Sauvegarder"
                        type="submit"
                        icon={FaSave}
                        className="bg-green-600 text-gray-200 gap-2"
                      />
                      <Button
                        text="Fermer"
                        onClick={() => setIsUploading(false)}
                        icon={FaTimes}
                        className="bg-red-600 text-gray-200 gap-2"
                      />
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
          <h2 className="text-xl">
            {user.prenom} {user.nom}
          </h2>
          <p className="text-gray-300">{user.email}</p>
        </div>
        <div className="flex justify-center">
          <Button
            text="Modifier les informations"
            onClick={handleEdit}
            className="bg-blue-600 text-gray-200"
          />
        </div>
        {/* <div className="my-4 flex items-center justify-center">
          <button
            onClick={handleToggleNotifications}
            className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors ${
              notificationsEnabled ? "bg-green-500" : "bg-gray-500"
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                notificationsEnabled ? "translate-x-8" : "translate-x-0"
              }`}
            >
              {notificationsEnabled ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-full w-full p-1 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.121V11a6 6 0 10-12 0v3.121c0 .417-.162.816-.447 1.104L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-full w-full p-1 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 5.482a7.499 7.499 0 015.885 5.885m1.76 1.76a7.5 7.5 0 01-9.405 9.405M3 3l18 18"
                  />
                </svg>
              )}
            </span>
          </button>
          <span className="ml-3 text-white">
            {notificationsEnabled
              ? "Notifications activées"
              : "Notifications désactivées"}
          </span>
        </div> */}

        {isEditing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-700 text-gray-200 p-6 rounded-lg shadow-xl max-w-lg w-full">
              <h2 className="text-xl font-bold uppercase p-3 mb-4 text-center text-gray-200 bg-primary rounded-md shadow">
                Modifier le Profil
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-gray-200">Nom</label>
                  <input
                    type="text"
                    name="nom"
                    value={user.nom}
                    onChange={handleChange}
                    className="border rounded p-2 w-full bg-gray-600 text-gray-200"
                  />
                </div>
                <div>
                  <label className="text-gray-200">Prénom</label>
                  <input
                    type="text"
                    name="prenom"
                    value={user.prenom}
                    onChange={handleChange}
                    className="border rounded p-2 w-full bg-gray-600 text-gray-200"
                  />
                </div>

                <div className="flex justify-center space-x-2">
                  <Button
                    text="Sauvegarder"
                    type="submit"
                    icon={FaSave}
                    className="bg-green-600 text-gray-200 gap-2"
                  />
                  <Button
                    text="Annuler"
                    onClick={handleCloseModal}
                    className="bg-red-600 text-gray-200 gap-2"
                    icon={FaTimes}
                  />
                </div>
                <p className="text-gray-400 text-sm">
                  Attention, vous devrez vous déconnecter et reconnecter après
                  les changements.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default Profile;
